import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import Icon from '../../components/AppIcon';
import GoBackButton from '../../components/GoBackButton';
import ProfessionalCard from './components/ProfessionalCard';
import CategoryFilter from './components/CategoryFilter';
import SearchFilterBar from './components/SearchFilterBar';
import AIRecommendations from './components/AIRecommendations';
import { professionalsService } from '../../services/supabaseService';
import { useLanguage } from '../../contexts/LanguageContext';

const ProfessionalMarketplace = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t } = useLanguage();
  const initialType = searchParams?.get('type') || 'all';
  const [activeCategory, setActiveCategory] = useState(initialType);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedState, setSelectedState] = useState('All States');
  const [sortBy, setSortBy] = useState('rating');
  const [professionals, setProfessionals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadProfessionals();
  }, []);

  const loadProfessionals = async () => {
    setLoading(true);
    const { data } = await professionalsService?.getAll();
    // Normalize data to match expected shape
    const normalized = (data || [])?.map(p => ({
      id: p?.id,
      category: p?.category,
      categoryLabel: p?.category ? (p?.category?.charAt(0)?.toUpperCase() + p?.category?.slice(1)) : '',
      name: p?.user?.full_name || p?.company || 'Professional',
      company: p?.company || '',
      location: p?.location || '',
      rating: parseFloat(p?.rating) || 0,
      reviewCount: p?.review_count || 0,
      verified: p?.verified || false,
      startingPrice: p?.starting_price || '',
      specializations: p?.specializations || [],
      phone: p?.phone || '',
      email: p?.user?.email || '',
      responseTime: p?.response_time || '',
      completedJobs: p?.completed_jobs || 0,
      repeatClients: p?.repeat_clients || '0%',
      certifications: p?.certifications || [],
      portfolio: (p?.portfolio || [])?.map(item => item?.image_url || item?.url || ''),
      availability: p?.availability || '',
      services: p?.services || [],
      reviews: (p?.reviews || [])?.map(r => ({
        author: r?.author_name,
        rating: r?.rating,
        date: r?.review_date,
        text: r?.review_text
      }))
    }));
    setProfessionals(normalized);
    setLoading(false);
  };

  const filtered = useMemo(() => {
    let list = professionals;
    if (activeCategory !== 'all') list = list?.filter(p => p?.category === activeCategory);
    if (selectedState !== 'All States') list = list?.filter(p => p?.location?.includes(selectedState));
    if (searchQuery) {
      const q = searchQuery?.toLowerCase();
      list = list?.filter(p =>
        p?.name?.toLowerCase()?.includes(q) ||
        p?.company?.toLowerCase()?.includes(q) ||
        p?.specializations?.some(s => s?.toLowerCase()?.includes(q))
      );
    }
    if (sortBy === 'rating') list = [...list]?.sort((a, b) => b?.rating - a?.rating);
    else if (sortBy === 'reviews') list = [...list]?.sort((a, b) => b?.reviewCount - a?.reviewCount);
    else if (sortBy === 'price_asc') list = [...list]?.sort((a, b) => parseInt(a?.startingPrice?.replace(/\D/g, '')) - parseInt(b?.startingPrice?.replace(/\D/g, '')));
    else if (sortBy === 'price_desc') list = [...list]?.sort((a, b) => parseInt(b?.startingPrice?.replace(/\D/g, '')) - parseInt(a?.startingPrice?.replace(/\D/g, '')));
    return list;
  }, [professionals, activeCategory, searchQuery, selectedState, sortBy]);

  const categoryStats = {
    all: professionals?.length,
    lawyer: professionals?.filter(p => p?.category === 'lawyer')?.length,
    photographer: professionals?.filter(p => p?.category === 'photographer')?.length,
    stylist: professionals?.filter(p => p?.category === 'stylist')?.length,
  };

  return (
    <>
      <Helmet><title>Find Professionals - ListingLens</title></Helmet>
      <div className="min-h-screen" style={{ background: '#050A14' }}>
        <div className="fixed inset-0 pointer-events-none" style={{ zIndex: 0 }}>
          <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 20% 20%, rgba(0,212,255,0.05) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(123,47,255,0.05) 0%, transparent 50%)' }} />
          <div className="absolute inset-0" style={{ backgroundImage: 'linear-gradient(rgba(0,212,255,0.03) 1px, transparent 1px), linear-gradient(90deg, rgba(0,212,255,0.03) 1px, transparent 1px)', backgroundSize: '60px 60px' }} />
        </div>

        <nav className="sticky top-0 z-40 px-4 py-3" style={{ background: 'rgba(5,10,20,0.95)', backdropFilter: 'blur(20px)', borderBottom: '1px solid rgba(0,212,255,0.15)' }}>
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex items-center gap-3">
              <button onClick={() => navigate('/')} className="flex items-center gap-2">
                <img src="/assets/images/logo_LL-1772022913346.jpeg" alt="ListingLens logo" className="h-9 w-auto rounded-lg object-cover" style={{ border: '1px solid rgba(0,212,255,0.4)', maxWidth: '120px' }} />
              </button>
              <span style={{ color: 'rgba(0,212,255,0.3)' }}>/</span>
              <span className="text-sm font-medium" style={{ color: '#00D4FF' }}>{t('findProfessionals')}</span>
            </div>
            <div className="flex items-center gap-3">
              <GoBackButton fallbackPath="/" label={t('goBack')} />
              <button onClick={() => navigate('/join-as-professional')} className="px-4 py-2 rounded-lg text-sm font-semibold" style={{ background: 'linear-gradient(135deg, #00D4FF, #0099CC)', color: '#000' }}>
                {t('joinAsProfessional')}
              </button>
              <button onClick={() => navigate('/')} className="p-2 rounded-lg" style={{ border: '1px solid rgba(0,212,255,0.2)', color: '#6B9EC4' }}>
                <Icon name="X" size={18} />
              </button>
            </div>
          </div>
        </nav>

        <div className="relative z-10 max-w-7xl mx-auto px-4 py-8">
          <div className="text-center mb-10">
            <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full mb-4 text-sm font-medium" style={{ background: 'rgba(0,212,255,0.1)', border: '1px solid rgba(0,212,255,0.2)', color: '#00D4FF' }}>
              <Icon name="Users" size={14} />
              {categoryStats?.all} {t('verifiedProfessionals')}
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-3">
              {t('findRightProfessional')?.split(' ')?.slice(0, -1)?.join(' ')} <span style={{ color: '#00D4FF' }}>{t('findRightProfessional')?.split(' ')?.slice(-1)?.[0]}</span>
            </h1>
            <p className="text-base max-w-xl mx-auto" style={{ color: '#6B9EC4' }}>
              {t('findProfessionalDesc')}
            </p>
          </div>

          <div className="grid grid-cols-3 gap-4 mb-8">
            {[
              { label: t('lawyers'), value: 'lawyer', count: categoryStats?.lawyer, icon: 'Scale', color: '#A78BFA', bg: 'rgba(123,47,255,0.1)', border: 'rgba(123,47,255,0.2)' },
              { label: t('photographers'), value: 'photographer', count: categoryStats?.photographer, icon: 'Camera', color: '#00D4FF', bg: 'rgba(0,212,255,0.1)', border: 'rgba(0,212,255,0.2)' },
              { label: t('stylists'), value: 'stylist', count: categoryStats?.stylist, icon: 'Palette', color: '#FF6B9D', bg: 'rgba(255,107,157,0.1)', border: 'rgba(255,107,157,0.2)' },
            ]?.map((stat) => (
              <button
                key={stat?.label}
                onClick={() => setActiveCategory(stat?.value)}
                className="p-4 rounded-xl text-center transition-all duration-200 hover:scale-105"
                style={{ background: stat?.bg, border: `1px solid ${stat?.border}` }}
              >
                <Icon name={stat?.icon} size={24} color={stat?.color} className="mx-auto mb-2" />
                <p className="text-2xl font-bold" style={{ color: stat?.color }}>{stat?.count}</p>
                <p className="text-xs" style={{ color: '#6B9EC4' }}>{stat?.label}</p>
              </button>
            ))}
          </div>

          <div className="rounded-xl p-4 mb-6 space-y-4" style={{ background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(0,212,255,0.1)' }}>
            <CategoryFilter activeCategory={activeCategory} onCategoryChange={setActiveCategory} />
            <SearchFilterBar searchQuery={searchQuery} onSearchChange={setSearchQuery} selectedState={selectedState} onStateChange={setSelectedState} sortBy={sortBy} onSortChange={setSortBy} />
          </div>

          <AIRecommendations professionals={filtered?.length > 0 ? filtered : professionals} />

          <div className="flex items-center justify-between mb-4">
            <p className="text-sm" style={{ color: '#6B9EC4' }}>
              {t('showingProfessionals')} <span className="text-white font-semibold">{filtered?.length}</span> {t('professionals')}
              {activeCategory !== 'all' && <span> in <span className="text-white capitalize">{activeCategory}s</span></span>}
            </p>
          </div>

          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2" style={{ borderColor: '#00D4FF' }} />
            </div>
          ) : filtered?.length === 0 ? (
            <div className="text-center py-16">
              <Icon name="Users" size={48} color="rgba(0,212,255,0.3)" className="mx-auto mb-4" />
              <p className="text-white font-semibold mb-2">{t('noProfessionalsFound')}</p>
              <p className="text-sm" style={{ color: '#6B9EC4' }}>{t('adjustFilters')}</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered?.map(pro => (
                <ProfessionalCard
                  key={pro?.id}
                  professional={pro}
                  onClick={() => navigate(`/professional-profile?id=${pro?.id}`)}
                />
              ))}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default ProfessionalMarketplace;
